"use client";

/**
 * Phone input that formats the number for the currently-selected country and
 * shows a country-accurate example as the placeholder. As-you-type grouping is
 * handled by react-phone-number-input; when the country changes we recompute the
 * example so the user always sees the exact format expected for that country.
 */
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import PhoneInput, {
  type Country,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { CountrySelect } from "../CountrySelect";

interface PhoneFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  defaultCountry?: Country;
}

export function PhoneField<T extends FieldValues>({
  control,
  name,
  label,
  defaultCountry = "SA",
}: PhoneFieldProps<T>) {
  const t = useTranslations("auth");
  const [country, setCountry] = useState<Country>(defaultCountry);

  // Country-accurate example, e.g. SA → "051 234 5678"
  const example = getExampleNumber(country, examples);
  const placeholder = example ? example.formatNational() : t("phonePlaceholder");

  return (
    <div>
      {label && (
        <label className="mb-2 block text-sm font-bold text-ink-900">{label}</label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          required: t("invalidPhone"),
          validate: (v) => !v || isValidPhoneNumber(v) || t("invalidPhone"),
        }}
        render={({ field, fieldState }) => (
          <>
            <PhoneInput
              {...field}
              defaultCountry={defaultCountry}
              onCountryChange={(c?: Country) => c && setCountry(c)}
              placeholder={placeholder}
              smartCaret
              className="PhoneInput"
              countrySelectComponent={CountrySelect}
            />
            {fieldState.error && (
              <p className="mt-1.5 px-1 text-xs text-red-500">
                {fieldState.error.message}
              </p>
            )}
          </>
        )}
      />
    </div>
  );
}
